export type TagId = number & { readonly __tagId: unique symbol };

export enum Quality {
  GOOD = 0,
  UNCERTAIN = 1,
  BAD = 2,
  FROZEN = 3,
}

export const QUALITY_NAMES: readonly string[] = ['GOOD', 'UNCERTAIN', 'BAD', 'FROZEN'];

export type TagKind =
  | 'measurement'
  | 'output'
  | 'setpoint'
  | 'state'
  | 'calculated'
  | 'discrete';

export interface TagMeta {
  id: TagId;
  name: string;
  units: string;
  rangeLo: number;
  rangeHi: number;
  decimals: number;
  description: string;
  trendable: boolean;
  kind: TagKind;
}

export interface TagSpec {
  name: string;
  units?: string;
  rangeLo?: number;
  rangeHi?: number;
  decimals?: number;
  description?: string;
  trendable?: boolean;
  kind?: TagKind;
  initial?: number;
}

/**
 * The tag database. Values live in one flat Float64Array so a snapshot is a transferable
 * buffer and hashing is order-stable (DECISIONS D-002).
 *
 * `set()` is the single choke point for CLAUDE.md rule 5: a non-finite write is refused,
 * the previous value is kept, and an EngineFault is raised.
 */
export class TagDb {
  values: Float64Array;
  quality: Uint8Array;
  private readonly metaList: TagMeta[] = [];
  private readonly index = new Map<string, TagId>();
  private capacity: number;
  private count = 0;
  private onNaN: ((tag: string, value: number) => void) | null = null;

  constructor(capacity = 1024) {
    this.capacity = capacity;
    this.values = new Float64Array(capacity);
    this.quality = new Uint8Array(capacity);
  }

  setNaNHandler(fn: (tag: string, value: number) => void): void {
    this.onNaN = fn;
  }

  get size(): number {
    return this.count;
  }

  get meta(): readonly TagMeta[] {
    return this.metaList;
  }

  private grow(): void {
    this.capacity *= 2;
    const v = new Float64Array(this.capacity);
    v.set(this.values);
    this.values = v;
    const q = new Uint8Array(this.capacity);
    q.set(this.quality);
    this.quality = q;
  }

  define(spec: TagSpec): TagId {
    const existing = this.index.get(spec.name);
    if (existing !== undefined) {
      throw new Error(`Duplicate tag "${spec.name}"`);
    }
    if (this.count >= this.capacity) this.grow();
    const id = this.count as TagId;
    this.metaList.push({
      id,
      name: spec.name,
      units: spec.units ?? '',
      rangeLo: spec.rangeLo ?? 0,
      rangeHi: spec.rangeHi ?? 100,
      decimals: spec.decimals ?? 2,
      description: spec.description ?? '',
      trendable: spec.trendable ?? true,
      kind: spec.kind ?? 'calculated',
    });
    this.index.set(spec.name, id);
    this.values[id] = spec.initial ?? 0;
    this.quality[id] = Quality.GOOD;
    this.count++;
    return id;
  }

  /** Define, or return the existing id if the tag already exists. */
  ensure(spec: TagSpec): TagId {
    const existing = this.index.get(spec.name);
    return existing !== undefined ? existing : this.define(spec);
  }

  has(name: string): boolean {
    return this.index.has(name);
  }

  id(name: string): TagId {
    const id = this.index.get(name);
    if (id === undefined) throw new Error(`Unknown tag "${name}"`);
    return id;
  }

  /** Resolve or return null — for optional config references. */
  find(name: string): TagId | null {
    return this.index.get(name) ?? null;
  }

  metaOf(id: TagId): TagMeta {
    const m = this.metaList[id];
    if (!m) throw new Error(`Unknown tag id ${id}`);
    return m;
  }

  get(id: TagId): number {
    return this.values[id] as number;
  }

  set(id: TagId, v: number): void {
    if (!Number.isFinite(v)) {
      this.onNaN?.(this.metaList[id]?.name ?? `#${id}`, v);
      return; // keep the previous value; the engine keeps running (rule 5)
    }
    this.values[id] = v;
  }

  getQuality(id: TagId): Quality {
    return this.quality[id] as Quality;
  }

  setQuality(id: TagId, q: Quality): void {
    this.quality[id] = q;
  }

  byName(name: string): number {
    return this.get(this.id(name));
  }

  /** Snapshot copy of the live value array, sized to the tags actually defined. */
  copyValues(into?: Float64Array): Float64Array {
    const out = into && into.length >= this.count ? into : new Float64Array(this.count);
    out.set(this.values.subarray(0, this.count));
    return out;
  }

  copyQuality(into?: Uint8Array): Uint8Array {
    const out = into && into.length >= this.count ? into : new Uint8Array(this.count);
    out.set(this.quality.subarray(0, this.count));
    return out;
  }

  serialise(): { v: number[]; q: number[] } {
    return {
      v: Array.from(this.values.subarray(0, this.count)),
      q: Array.from(this.quality.subarray(0, this.count)),
    };
  }

  restore(s: { v: number[]; q: number[] }): void {
    for (let i = 0; i < this.count; i++) {
      const val = s.v[i];
      this.values[i] = Number.isFinite(val) ? (val as number) : 0;
      this.quality[i] = s.q[i] ?? Quality.GOOD;
    }
  }
}
